import { all, put, select, takeEvery, fork, call } from 'redux-saga/effects';
import { newPageLoaded, logLastAdRefresh, setPageSafety } from './actions';
import { getLastRefresh, getAdsRefreshCount } from './selectors';
import ddt from '@wix/da-ddt';
import { adsRefreshTimeout, maxAdsRefresh, mightBeBotVersion } from './constants';
import { getAdsEnabled, getIsLoggedIn } from '../../publicSession/selectors';
import { load } from '../bot/botd.js';
import * as Cookie from 'js-cookie';
import { logBiEvent } from '../../biLogger/redux/saga';
import { PureBiData } from '@wix/da-bi/pkg/events';
function canRefresh(last) {
    if (performance.now() - last >= adsRefreshTimeout) {
        return true;
    }
    return false;
}
/**
 * This signals a new pageview to freestar.
 *
 * This refreshes ads when the page url changes, but only once every 30s. It does
 * not handle timed refresh, that is handled by freestar and is set to much longer
 * currently 4m.
 *
 * The idea is to maintain good viewability and make sure we get fresh ads when the
 * main content changes but not too often if the user is moving fast.
 *
 * Total refreshes are also capped to 60 currently.
 */ function* handlePageLoaded() {
    const adsEnabled = yield select(getAdsEnabled);
    const lastRefresh = yield select(getLastRefresh);
    const refreshCount = yield select(getAdsRefreshCount);
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!adsEnabled || typeof window === 'undefined') {
        return;
    }
    // Signal for new pageview
    if (window['freestar']) {
        var _window_freestar_queue, _window_freestar;
        (_window_freestar = window['freestar']) === null || _window_freestar === void 0 ? void 0 : (_window_freestar_queue = _window_freestar.queue) === null || _window_freestar_queue === void 0 ? void 0 : _window_freestar_queue.push(function() {
            window['freestar'].trackPageview();
            ddt.log('ads', 'FS pageview');
        });
    }
    if (window['htlbid']) {
        window['htlbid'].cmd.push(()=>{
            window['htlbid'].newPage();
        });
    }
    // Handle refreshing after page changes
    if (canRefresh(lastRefresh) && refreshCount <= maxAdsRefresh) {
        // Loggedin refresh
        if (isLoggedIn && window['googletag']) {
            var _window_googletag_queue, _window_googletag;
            (_window_googletag = window['googletag']) === null || _window_googletag === void 0 ? void 0 : (_window_googletag_queue = _window_googletag.queue) === null || _window_googletag_queue === void 0 ? void 0 : _window_googletag_queue.push(function() {
                try {
                    // Get the ids inview
                    const inview = document.querySelectorAll('[data-inview=true]');
                    const ids = [];
                    for (const el of Array.from(inview)){
                        ids.push(el.id);
                    }
                    // Get the GPT slots
                    const slots = window['googletag'].pubads().getSlots();
                    if (Array.isArray(slots)) {
                        const toRefresh = [];
                        for (const slot of slots){
                            if (ids.includes(slot.getSlotElementId())) {
                                toRefresh.push(slot);
                            }
                        }
                        if (toRefresh.length > 0) {
                            window['googletag'].pubads().refresh(toRefresh);
                        }
                    }
                } catch (e) {}
            });
        }
        // Loggedout refresh
        if (!isLoggedIn && window['freestar']) {
            var _window_freestar_queue1, _window_freestar1;
            (_window_freestar1 = window['freestar']) === null || _window_freestar1 === void 0 ? void 0 : (_window_freestar_queue1 = _window_freestar1.queue) === null || _window_freestar_queue1 === void 0 ? void 0 : _window_freestar_queue1.push(function() {
                window['freestar'].refreshActiveSlots();
                ddt.log('ads', 'Ads Refresh Triggered');
            });
        }
        // hashtag refresh
        // TODO
        yield put(logLastAdRefresh(performance.now()));
    }
}
function setMBBCookie() {
    Cookie.set('mbb', mightBeBotVersion, {
        domain: `.deviantart.${process.env.NODE_ENV === 'development' ? 'lan' : 'com'}`,
        sameSite: 'lax'
    });
}
function* basicBotDetection() {
    if (typeof window === 'undefined') {
        return;
    }
    if (Cookie.get('mbb') === mightBeBotVersion) {
        ddt.log('ads', 'Browser is bot turning off ads cookied');
        yield put(setPageSafety({
            safety: 'off'
        }));
        return;
    }
    try {
        if (!/deviantart\.(com|lan|lan:3000)$/i.test(window.location.host)) {
            yield put(setPageSafety({
                safety: 'off'
            }));
            ddt.log('ads', 'Ads bot detected proxy', [
                'ads'
            ], {});
            setMBBCookie();
            yield call(logBiEvent, PureBiData({
                evid: 3,
                bot_kind: 'proxy',
                bot_trap: 'proxy'
            }), false);
            return;
        }
        const botd = yield load();
        const result = yield botd.detect();
        if (result && result.bot === true) {
            const detections = botd.getDetections();
            const detectKeys = Object.keys(detections);
            const detectionString = [];
            if (detectKeys.length > 0) {
                detectKeys.forEach((d)=>{
                    if (detections[d].bot === true) {
                        detectionString.push(d);
                    }
                });
            }
            // This detector relies on focus and we don't think this is reliable enough
            // when opening new tabs etc So we require more than one detection for this trap.
            if (detectionString.includes('detectWindowSize') && detectionString.length === 1) {
                return;
            }
            ddt.log('ads', 'Browser is bot turning off ads', result);
            yield call(logBiEvent, PureBiData({
                evid: 3,
                bot_kind: result.botKind,
                bot_trap: detectionString.join(',')
            }), false);
            yield put(setPageSafety({
                safety: 'off'
            }));
            setMBBCookie();
        }
    } catch (e) {}
}
export default function* adsSaga() {
    yield fork(basicBotDetection);
    yield all([
        takeEvery(newPageLoaded, handlePageLoaded)
    ]);
}
